import { selectPartner360Data } from 'legacy/lut/features/partner360/configuration'
import type { ReactNode } from 'react'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import { PLANNING_COCKPIT_TOUR_TARGETS as TOUR_TARGETS } from 'partner360/configs/widgetTour/planningCockpitTourTargets'

import {
  selectCockpitGeoMappingItems,
  selectCockpitOffering,
  selectCockpitPartnerData,
} from '../../model/selectors'
import { COCKPIT_DRAWER_HOST_SELECTOR } from '../components/cockpitDrawer'

import { PARTNER_EXTENDED_INFO_ID, PartnerExtendedInfo, PartnerSummaryCard } from './components'
import {
  getCockpitPartnerMarketShortName,
  getCockpitPartnerMemberships,
  getCockpitPartnerProfileGroups,
  getCockpitPartnerRegionLabel,
} from './lib/getCockpitPartnerDetails'

import styles from './CockpitPartnerDetails.module.scss'

interface ICockpitPartnerDetailsProps {
  /** A card set at the top of the column, over the partner summary — the cockpit's scope. */
  scope?: ReactNode
  /** Cards set under the partner summary, e.g. the sales team. */
  children?: ReactNode
}

export const CockpitPartnerDetails = ({ scope, children }: ICockpitPartnerDetailsProps) => {
  const rootRef = useRef<HTMLElement>(null)
  const partnerData = useSelector(selectPartner360Data)
  const selectedOffering = useSelector(selectCockpitOffering)
  const geoMappingItems = useSelector(selectCockpitGeoMappingItems)
  const cockpitPartners = useSelector(selectCockpitPartnerData)
  const partnerName =
    cockpitPartners.find(({ PartnerId }) => PartnerId === partnerData.partnerId)?.PartnerName ||
    partnerData.partnerName
  const [isExtendedInfoOpen, setIsExtendedInfoOpen] = useState(false)

  const profileGroups = useMemo(
    () => getCockpitPartnerProfileGroups(partnerData.p360Profiles, selectedOffering),
    [partnerData.p360Profiles, selectedOffering]
  )
  const memberships = useMemo(
    () =>
      getCockpitPartnerMemberships(
        partnerData.amplifyTrack,
        partnerData.partnerAccess.isPowerElite,
        partnerData.partnerAccess.isPowerEliteGlobal
      ),
    [
      partnerData.amplifyTrack,
      partnerData.partnerAccess.isPowerElite,
      partnerData.partnerAccess.isPowerEliteGlobal,
    ]
  )
  const regionLabel = useMemo(
    () =>
      getCockpitPartnerRegionLabel(
        getCockpitPartnerMarketShortName(geoMappingItems, partnerData.geoMarket),
        partnerData.country
      ),
    [geoMappingItems, partnerData.geoMarket, partnerData.country]
  )

  const toggleExtendedInfo = useCallback(() => setIsExtendedInfoOpen((isOpen) => !isOpen), [])
  /* Focus goes back to the toggle the drawer was opened from, as it does for Help & tools. */
  const closeExtendedInfo = useCallback(() => {
    setIsExtendedInfoOpen(false)
    rootRef.current
      ?.querySelector<HTMLButtonElement>(`[aria-controls="${PARTNER_EXTENDED_INFO_ID}"]`)
      ?.focus()
  }, [])
  const getDrawerHost = useCallback(
    () => rootRef.current?.closest<HTMLElement>(COCKPIT_DRAWER_HOST_SELECTOR),
    []
  )

  return (
    <aside ref={rootRef} className={styles.root} aria-label={'Partner details'}>
      {scope}
      {/* The tour spotlights the column card by card, so the id marks this card rather than
          the column. */}
      <PartnerSummaryCard
        partnerName={partnerName}
        regionLabel={regionLabel}
        partnerId={partnerData.partnerId}
        locationId={partnerData.locationId}
        grades={profileGroups.grades}
        memberships={memberships}
        specializations={profileGroups.specializations}
        programs={profileGroups.programs}
        isExtendedInfoOpen={isExtendedInfoOpen}
        onExtendedInfoToggle={toggleExtendedInfo}
        tourId={TOUR_TARGETS.partnerDetails}
      />
      {children}
      <PartnerExtendedInfo
        partnerId={partnerData.partnerId}
        isOpen={isExtendedInfoOpen}
        onClose={closeExtendedInfo}
        getHost={getDrawerHost}
      />
    </aside>
  )
}

CockpitPartnerDetails.displayName = 'CockpitPartnerDetails'
